import { useState, useRef, useEffect } from 'react';
import type { Category } from '../types/confession';
import { CATEGORIES } from '../types/confession';
import type { SortOption, TimePeriod } from '../lib/api';
import { hapticFeedback } from '../lib/telegram';

interface FilterBarProps {
  selectedCategory: Category | 'all';
  selectedSort: SortOption;
  selectedPeriod: TimePeriod;
  onCategoryChange: (category: Category | 'all') => void;
  onSortChange: (sort: SortOption) => void;
  onPeriodChange: (period: TimePeriod) => void;
}

const CATEGORY_OPTIONS: { id: Category | 'all'; label: string; emoji: string }[] = [
  { id: 'all', label: 'All', emoji: '🌟' },
  ...CATEGORIES.map(c => ({ id: c.id, label: c.label, emoji: c.emoji })),
];

const SORT_OPTIONS: { id: SortOption; label: string; emoji: string }[] = [
  { id: 'new', label: 'New', emoji: '⏰' },
  { id: 'hot', label: 'Hot', emoji: '🔥' },
  { id: 'top', label: 'Top', emoji: '👑' },
];

const PERIOD_OPTIONS: { id: TimePeriod; label: string; emoji: string }[] = [
  { id: 'day', label: 'Today', emoji: '📅' },
  { id: 'week', label: 'This Week', emoji: '📆' },
  { id: 'all', label: 'All Time', emoji: '🕰️' },
];

type DropdownType = 'category' | 'sort' | 'period' | null;

export function FilterBar({
  selectedCategory,
  selectedSort,
  selectedPeriod,
  onCategoryChange,
  onSortChange,
  onPeriodChange,
}: FilterBarProps) {
  const [openDropdown, setOpenDropdown] = useState<DropdownType>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleDropdownToggle = (type: DropdownType) => {
    hapticFeedback('light');
    setOpenDropdown(openDropdown === type ? null : type);
  };

  const handleCategorySelect = (category: Category | 'all') => {
    hapticFeedback('medium');
    onCategoryChange(category);
    setOpenDropdown(null);
  };

  const handleSortSelect = (sort: SortOption) => {
    hapticFeedback('medium');
    onSortChange(sort);
    setOpenDropdown(null);
  };

  const handlePeriodSelect = (period: TimePeriod) => {
    hapticFeedback('medium');
    onPeriodChange(period);
    setOpenDropdown(null);
  };

  const currentCategory = CATEGORY_OPTIONS.find(c => c.id === selectedCategory)!;
  const currentSort = SORT_OPTIONS.find(s => s.id === selectedSort)!;
  const currentPeriod = PERIOD_OPTIONS.find(p => p.id === selectedPeriod)!;

  const buttonBaseClass = "flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all border";
  const buttonInactiveClass = "bg-[#1a1a2e] border-white/10 text-gray-200 hover:border-white/20 hover:bg-[#252542]";
  const buttonActiveClass = "bg-purple-600/30 border-purple-500/50 text-white";

  const dropdownClass = "absolute top-full left-0 mt-1.5 bg-[#1a1a2e] border border-white/15 rounded-xl shadow-xl overflow-hidden z-50";

  return (
    <div className="flex gap-2 flex-wrap" ref={containerRef}>
      {/* Category Dropdown */}
      <div className="relative">
        <button
          onClick={() => handleDropdownToggle('category')}
          className={`${buttonBaseClass} ${openDropdown === 'category' ? buttonActiveClass : buttonInactiveClass}`}
        >
          <span>{currentCategory.emoji}</span>
          <span>{currentCategory.label}</span>
          <span className={`text-[10px] ml-0.5 transition-transform ${openDropdown === 'category' ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {openDropdown === 'category' && (
          <div className={`${dropdownClass} min-w-[140px] max-h-[280px] overflow-y-auto`}>
            {CATEGORY_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => handleCategorySelect(option.id)}
                className={`w-full flex items-center gap-2 px-3.5 py-2.5 text-left text-sm transition-colors ${
                  selectedCategory === option.id
                    ? 'bg-purple-600/30 text-purple-200'
                    : 'text-gray-300 hover:bg-white/10'
                }`}
              >
                <span>{option.emoji}</span>
                <span>{option.label}</span>
                {selectedCategory === option.id && (
                  <span className="ml-auto text-purple-400">✓</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Sort Dropdown */}
      <div className="relative">
        <button
          onClick={() => handleDropdownToggle('sort')}
          className={`${buttonBaseClass} ${openDropdown === 'sort' ? buttonActiveClass : buttonInactiveClass}`}
        >
          <span>{currentSort.emoji}</span>
          <span>{currentSort.label}</span>
          <span className={`text-[10px] ml-0.5 transition-transform ${openDropdown === 'sort' ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {openDropdown === 'sort' && (
          <div className={`${dropdownClass} min-w-[120px]`}>
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => handleSortSelect(option.id)}
                className={`w-full flex items-center gap-2 px-3.5 py-2.5 text-left text-sm transition-colors ${
                  selectedSort === option.id
                    ? 'bg-purple-600/30 text-purple-200'
                    : 'text-gray-300 hover:bg-white/10'
                }`}
              >
                <span>{option.emoji}</span>
                <span>{option.label}</span>
                {selectedSort === option.id && (
                  <span className="ml-auto text-purple-400">✓</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Time Period Dropdown */}
      <div className="relative">
        <button
          onClick={() => handleDropdownToggle('period')}
          className={`${buttonBaseClass} ${openDropdown === 'period' ? buttonActiveClass : buttonInactiveClass}`}
        >
          <span>{currentPeriod.emoji}</span>
          <span>{currentPeriod.label}</span>
          <span className={`text-[10px] ml-0.5 transition-transform ${openDropdown === 'period' ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {openDropdown === 'period' && (
          <div className={`${dropdownClass} min-w-[130px]`}>
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => handlePeriodSelect(option.id)}
                className={`w-full flex items-center gap-2 px-3.5 py-2.5 text-left text-sm transition-colors ${
                  selectedPeriod === option.id
                    ? 'bg-purple-600/30 text-purple-200'
                    : 'text-gray-300 hover:bg-white/10'
                }`}
              >
                <span>{option.emoji}</span>
                <span>{option.label}</span>
                {selectedPeriod === option.id && (
                  <span className="ml-auto text-purple-400">✓</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
